import {
  lazy,
  type ComponentType,
  type LazyExoticComponent,
} from "react";
import { ROUTE_PREFETCH_LOADERS, type RouteChunkLoader } from "@/lib/constants/routes/route-prefetch";
import { normalizeLegacyRoutePath } from "@/lib/constants/routes/route-paths";

const CHUNK_RELOAD_KEY_PREFIX = "9s:chunk-reload:";
const CHUNK_RELOAD_AT_SUFFIX = ":at";
const CHUNK_RELOAD_COOLDOWN_MS = 10_000;

const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /failed to load module script/i,
  /unable to preload css/i,
  /loading chunk [\w.-]+ failed/i,
  /chunkloaderror/i,
  /css_chunk_load_failed/i,
  /expected a javascript(?:-or-wasm)? module script but the server responded with a mime type of ["']?text\/html/i,
  /mime type .+text\/html.+(?:module|javascript)/i,
];

let autoReloadOverride: boolean | null = null;

export function __setChunkRecoveryAutoReloadForTests(enabled: boolean | null): void {
  autoReloadOverride = enabled;
}

function shouldAutoReloadOnChunkError(): boolean {
  if (autoReloadOverride !== null) {
    return autoReloadOverride;
  }
  return Boolean(import.meta.env.PROD);
}

export function getAppBuildId(): string {
  const fromEnv = String(import.meta.env.VITE_APP_BUILD_ID ?? "").trim();
  if (fromEnv.length > 0) {
    return fromEnv;
  }
  return "dev";
}

export function chunkReloadStorageKey(buildId: string = getAppBuildId()): string {
  return `${CHUNK_RELOAD_KEY_PREFIX}${buildId}`;
}

function chunkReloadAtStorageKey(buildId: string = getAppBuildId()): string {
  return `${chunkReloadStorageKey(buildId)}${CHUNK_RELOAD_AT_SUFFIX}`;
}

export function isChunkLoadError(error: unknown): boolean {
  if (error == null) {
    return false;
  }
  const name =
    typeof error === "object" && error !== null && "name" in error
      ? String((error as { name?: unknown }).name ?? "")
      : "";
  const message =
    typeof error === "object" && error !== null && "message" in error
      ? String((error as { message?: unknown }).message ?? "")
      : String(error);
  const haystack = `${name} ${message}`.trim();
  if (haystack.length === 0) {
    return false;
  }
  if (CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(haystack))) {
    return true;
  }
  const citesHashedAsset = /\/assets\/[\w.@%-]+\.(?:js|css)\b/i.test(haystack);
  const looksLikeLoadFailure = /(?:failed|error|load|fetch|import|preload|module)/i.test(haystack);
  return citesHashedAsset && looksLikeLoadFailure;
}

function hasReloadedForBuild(buildId: string = getAppBuildId()): boolean {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
    return false;
  }
  try {
    if (sessionStorage.getItem(chunkReloadStorageKey(buildId)) === "1") {
      return true;
    }
    const rawAt = sessionStorage.getItem(chunkReloadAtStorageKey(buildId));
    if (rawAt === null) {
      return false;
    }
    const lastAt = Number(rawAt);
    return Number.isFinite(lastAt) && Date.now() - lastAt < CHUNK_RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markReloadedForBuild(buildId: string = getAppBuildId()): void {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
    return;
  }
  try {
    sessionStorage.setItem(chunkReloadStorageKey(buildId), "1");
    sessionStorage.setItem(chunkReloadAtStorageKey(buildId), String(Date.now()));
  } catch {
    return;
  }
}

export function clearChunkReloadMarker(buildId: string = getAppBuildId()): void {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
    return;
  }
  try {
    sessionStorage.removeItem(chunkReloadStorageKey(buildId));
  } catch {
    return;
  }
}

export function reloadOnceForChunkError(reason: string, error?: unknown): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  if (!shouldAutoReloadOnChunkError()) {
    return false;
  }
  if (error !== undefined && !isChunkLoadError(error)) {
    return false;
  }
  const buildId = getAppBuildId();
  if (hasReloadedForBuild(buildId)) {
    return false;
  }
  markReloadedForBuild(buildId);
  console.info(`[chunk-recovery] reload once (${reason}) build=${buildId}`);
  window.location.reload();
  return true;
}

export function lazyRecover<TModule>(importer: () => Promise<TModule>): Promise<TModule> {
  return importer().catch((error: unknown) => {
    if (isChunkLoadError(error)) {
      const reloaded = reloadOnceForChunkError("lazyRecover", error);
      if (reloaded) {
        return new Promise<TModule>(() => undefined);
      }
    }
    throw error;
  });
}

type RecoverableComponent<T> = T extends ComponentType<infer TProps>
  ? T & ComponentType<TProps>
  : never;

export function lazyWithChunkRecovery<T>(
  importer: () => Promise<{ default: T }>,
): LazyExoticComponent<RecoverableComponent<T>> {
  const typedImporter = importer as () => Promise<{ default: ComponentType<object> }>;
  return lazy(() => lazyRecover(typedImporter)) as LazyExoticComponent<RecoverableComponent<T>>;
}

const prefetchedRoutes = new Set<string>();
const prefetchInFlight = new Set<string>();

function resolveRoutePrefetchLoaders(pathname: string): readonly RouteChunkLoader[] {
  const normalized = normalizeLegacyRoutePath(pathname.split("?")[0] || "/");
  let selected: (typeof ROUTE_PREFETCH_LOADERS)[number] | null = null;
  for (const entry of ROUTE_PREFETCH_LOADERS) {
    if (normalized === entry.prefix || normalized.startsWith(`${entry.prefix}/`)) {
      if (!selected || entry.prefix.length > selected.prefix.length) {
        selected = entry;
      }
    }
  }
  return selected?.loaders ?? [];
}

export function prefetchRoute(pathname: string): void {
  if (typeof window === "undefined") {
    return;
  }
  const normalized = normalizeLegacyRoutePath(pathname.split("?")[0] || "/");
  if (prefetchedRoutes.has(normalized) || prefetchInFlight.has(normalized)) {
    return;
  }
  const loaders = resolveRoutePrefetchLoaders(normalized);
  if (loaders.length === 0) {
    return;
  }
  prefetchInFlight.add(normalized);
  void Promise.all(
    loaders.map((loader) =>
      loader().catch((error: unknown) => {
        if (import.meta.env.DEV) {
          console.info("[prefetch]", normalized, error);
        }
        return undefined;
      }),
    ),
  )
    .then(() => {
      prefetchedRoutes.add(normalized);
    })
    .finally(() => {
      prefetchInFlight.delete(normalized);
    });
}

export function createRoutePrefetchHandlers(pathname: string): {
  onMouseEnter: () => void;
  onFocus: () => void;
} {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const schedulePrefetch = () => {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      const run = () => prefetchRoute(pathname);
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(run);
        return;
      }
      run();
    }, 75);
  };
  return {
    onMouseEnter: schedulePrefetch,
    onFocus: schedulePrefetch,
  };
}

export function __resetRoutePrefetchStateForTests(): void {
  prefetchedRoutes.clear();
  prefetchInFlight.clear();
}

export function __isRoutePrefetchedForTests(pathname: string): boolean {
  const normalized = normalizeLegacyRoutePath(pathname.split("?")[0] || "/");
  return prefetchedRoutes.has(normalized);
}

let listenersInstalled = false;

export function installChunkRecoveryListeners(): void {
  if (typeof window === "undefined" || listenersInstalled) {
    return;
  }
  listenersInstalled = true;

  window.addEventListener("vite:preloadError", ((event: Event) => {
    const preloadEvent = event as Event & { preventDefault?: () => void; payload?: unknown };
    if (typeof preloadEvent.preventDefault === "function") {
      preloadEvent.preventDefault();
    }
    if ("payload" in preloadEvent && preloadEvent.payload !== undefined) {
      reloadOnceForChunkError("vite:preloadError", preloadEvent.payload);
      return;
    }
    reloadOnceForChunkError("vite:preloadError");
  }) as EventListener);

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    if (!isChunkLoadError(event.reason)) {
      return;
    }
    if (reloadOnceForChunkError("unhandledrejection", event.reason)) {
      event.preventDefault();
    }
  });
}
